import { Text } from "pixi.js";
import gsap from "gsap";

export type TextCounterControllerOptions = {
  attachTo: Text;
  startingValue: number;
  jackpotMode: boolean;
  duration?: number;
  delay?: number;
  maxWidth?: number;
  maxHeight?: number;
  onBegin?: (() => void) | undefined;
  onUpdate?: (() => void) | undefined;
  onComplete?: (() => void) | undefined;
  formatter?: (value: number) => string;
};

export type ControlledTextMap = {
  [id: string]: {
    text: Text;
    controller: TextCounterController;
  };
};

type CounterData = {
  value: number;
};

export class TextCounterController {
  private attachedText: Text | undefined;
  private _startValue: number = 0;
  private _endValue: number = 0;
  private _delay: number = 0;
  public jackpotMode: boolean = false;
  private _duration: number = 0;

  private maxWidth: number = 0;
  private maxHeight: number = 0;
  private originalFontSize?: number;

  private countTimeline: gsap.core.Timeline | undefined;
  private showTextTween: gsap.core.Tween | undefined;
  private formatter?: (value: number) => string;

  private onBegin?: (() => void) | undefined;
  private onUpdate?: (() => void) | undefined;
  private onComplete?: (() => void) | undefined;

  /**
   * Minimum speed of timer, used to calculate delay and speed of animation on small spin changes
   * in digits per second
   * @type {number}
   */
  private minSpeed: number = 0;

  private counterData: CounterData = {
    value: 0,
  };

  constructor(options?: TextCounterControllerOptions) {
    if (options !== undefined) {
      this.attachedText = options.attachTo;
      this._startValue = options.startingValue;
      this._endValue = options.startingValue;
      this.jackpotMode = options.jackpotMode;
      this._delay = options.delay ?? 0;
      this._duration = options.duration ?? 0;
      this.maxWidth = options.maxWidth ?? 0;
      this.maxHeight = options.maxHeight ?? 0;
      this.onBegin = options.onBegin;
      this.onUpdate = options.onUpdate;
      this.onComplete = options.onComplete;

      this.counterData.value = this._startValue;
    }

    if (options?.formatter) {
      this.formatter = options.formatter;
    } else {
      this.formatter = (value: number): string => {
        const formatter = new Intl.NumberFormat();
        return formatter.format(value);
      };
    }

    if (this.attachedText) {
      this.originalFontSize = this.attachedText.style.fontSize;
      this.attachedText.alpha = 0;

      this.showTextTween = gsap
        .fromTo(
          this.attachedText,
          { alpha: 0 },
          {
            alpha: 1,
            duration: 0.2,
          },
        )
        .pause(0);
    }
  }

  value = (): number => {
    return this.counterData.value;
  };

  textField = (): Text | undefined => {
    return this.attachedText;
  };

  countTo = (endValue: number, duration?: number, delay?: number): void => {
    this._startValue = this.counterData.value;
    this._endValue = endValue;

    this.spin(delay ?? this._delay, duration ?? this._duration);
  };

  countFromTo = (
    startValue: number,
    endValue: number,
    duration?: number,
    delay?: number,
  ): void => {
    this.counterData.value = startValue;
    this._startValue = startValue;
    this._endValue = endValue;
    // this.animationTime = duration;

    this.spin(delay ?? this._delay, duration ?? this._duration);
  };

  private spin = (delay: number = 0, duration: number = 1) => {
    if (this.countTimeline) {
      this.countTimeline.kill();
    }

    // If text would spin slower than minSpeed, then we calculate min speed
    // and add the difference in duration to delay. In this way the spin animation
    // should still end at the end of original duration + delay.
    if (this.jackpotMode && this.minSpeed > 0) {
      const delta =
        Math.max(this._startValue, this._endValue) -
        Math.min(this._startValue, this._endValue);

      const integersPerSecond = delta / duration;

      if (integersPerSecond < this.minSpeed) {
        const minDuration = delta / integersPerSecond;
        const durationDelta = duration - minDuration;

        delay += durationDelta;
        duration = minDuration;
      }
    }

    this.countTimeline = gsap.timeline();

    const anims = [
      gsap.fromTo(
        this.counterData,
        { value: this._startValue },
        {
          value: this._endValue,
          // delay: delay,
          duration: duration,
          onStart: () => {
            this.onBegin?.();
            if (this._startValue === 0) {
              this.showTextTween?.play(0);
            }
          },
          onComplete: () => {
            this.onComplete?.();
          },
          onUpdate: () => {
            if (this.attachedText !== undefined) {
              let text;
              if (this.formatter !== undefined) {
                text = this.formatter(this.counterData.value);
              } else {
                text = `${this.counterData.value.toFixed(2)}`;
              }
              this.attachedText.text = `${text}`;
              if (this.maxWidth > 0) {
                while (this.attachedText.width > this.maxWidth) {
                  this.attachedText.style.fontSize -= 1;
                }
              }
              if (this.maxHeight > 0) {
                while (this.attachedText.height > this.maxHeight) {
                  this.attachedText.style.fontSize -= 1;
                }
              }
            }
            this.onUpdate?.();
          },
          ease: "none",
        },
      ),
    ];

    this.countTimeline.add(anims);
  };

  public stop = (): void => {
    if (this.countTimeline !== undefined) {
      this.countTimeline.pause().clear();
      this.countTimeline = undefined;
    }
  };

  /**
   * Destroys the TextCounterController and cleans up all resources
   */
  public destroy = (): void => {
    // Kill the count timeline
    if (this.countTimeline !== undefined) {
      this.countTimeline.kill();
      this.countTimeline = undefined;
    }

    // Kill the show text tween
    if (this.showTextTween !== undefined) {
      this.showTextTween.kill();
      this.showTextTween = undefined;
    }

    // Clear text reference
    this.attachedText = undefined;

    // Clear callbacks
    this.onBegin = undefined;
    this.onComplete = undefined;
  };

  public get duration(): number {
    return this._duration;
  }

  public get delay(): number {
    return this._delay;
  }

  public get startValue(): number {
    return this._startValue;
  }

  public get endValue(): number {
    return this._endValue;
  }
}
